import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { COMMANDS, SlashCommand } from './commands.ts';

interface CommandPaletteProps {
  query: string;               // texte après "/" pour filtrer
  onSelect: (command: string) => void;
  onClose: () => void;
}

export interface CommandPaletteRef {
  handleKeyDown: (e: React.KeyboardEvent) => boolean;
}

const CommandPalette = forwardRef<CommandPaletteRef, CommandPaletteProps>(
  ({ query, onSelect, onClose }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Filtrer les commandes : commence par '/' + query (case-insensitive)
    const filteredCommands = COMMANDS.filter((cmd) =>
      cmd.command.toLowerCase().startsWith(('/' + query).toLowerCase())
    );

    // Réinitialiser l'index de sélection lorsque la liste filtrée change
    useEffect(() => {
      setSelectedIndex(0);
    }, [query]);

    // Gérer le défilement automatique pour l'élément sélectionné
    useEffect(() => {
      if (itemRefs.current[selectedIndex]) {
        itemRefs.current[selectedIndex]?.scrollIntoView({
          block: 'nearest',
        });
      }
    }, [selectedIndex]);

    // Exposer la méthode de gestion du clavier au parent via ref
    useImperativeHandle(ref, () => ({
      handleKeyDown(e: React.KeyboardEvent): boolean {
        if (filteredCommands.length === 0) return false;

        switch (e.key) {
          case 'ArrowDown':
            setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
            return true;
          case 'ArrowUp':
            setSelectedIndex(
              (prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length
            );
            return true;
          case 'Enter':
            onSelect(filteredCommands[selectedIndex].command);
            return true;
          case 'Escape':
            onClose();
            return true;
          default:
            return false;
        }
      },
    }));

    if (filteredCommands.length === 0) {
      return null;
    }

    return (
      <div className="absolute bottom-full left-0 w-full mb-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-20 max-h-60 overflow-y-auto">
        {filteredCommands.map((item, index) => (
          <div
            key={item.command}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            onClick={() => onSelect(item.command)}
            className={`flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer hover:bg-accent transition-colors ${
              index === selectedIndex ? 'bg-accent' : ''
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="font-mono font-medium text-primary">{item.command}</span>
              <span className="text-muted-foreground text-xs">({item.label})</span>
            </div>
            <span className="text-muted-foreground text-xs">{item.description}</span>
          </div>
        ))}
      </div>
    );
  }
);

CommandPalette.displayName = 'CommandPalette';

export default CommandPalette;
